import { CoreMenu } from '@core/types';
import { IUser } from 'app/interfaces/user.interface';
import { ApiRoutes } from 'app/routes/app.routes';
import { ActiveUser, isLearner, isStaff, isAdmin } from 'app/utils/helpers.utils';

const user: IUser = ActiveUser();

export const menu: CoreMenu[] = [
  // Dashboard - Accessible to all authenticated users
  {
    id: 'Dashboard',
    title: 'Dashboard',
    translate: 'Dashboard',
    type: 'item',
    icon: 'grid',
    url: 'app/dashboard',
    role: [ApiRoutes.api.statistics.all],
  },

  // Profile Section - Role-specific
  (isLearner()) && {
    id: 'learner-profile',
    title: 'My Profile',
    type: 'item',
    icon: 'user',
    url: 'app/learners/profile/' + user.id,
    role: [ApiRoutes.api.learner.retrieveSingle],
  },
  (isStaff()) && {
    id: 'teacher-profile',
    title: 'My Profile',
    type: 'item',
    icon: 'user',
    url: 'app/staff/profile/' + user.id,
    role: [ApiRoutes.api.teacher.retrieveSingle],
  },

  // Academic Management - Admin & Staff Access
  (isStaff() || isAdmin()) && {
    id: 'academic-management',
    title: 'Academic Management',
    type: 'collapsible',
    icon: 'book-open',
    role: [ApiRoutes.api.course.retrieve, ApiRoutes.api.classroom.retrieve],
    children: [
      {
        id: 'classrooms',
        title: 'Classrooms',
        type: 'collapsible',
        icon: 'home',
        role: [ApiRoutes.api.classroom.retrieve],
        children: [
          {
            id: 'classrooms-list',
            title: 'All Classrooms',
            type: 'item',
            icon: 'home',
            url: 'app/classrooms',
            role: [ApiRoutes.api.classroom.retrieve],
          },
          {
            id: 'classroom-progression',
            title: 'Progression Config',
            type: 'item',
            icon: 'git-branch',
            url: 'app/classroom-progression/config',
            role: [ApiRoutes.api.classroom.edit],
          },
          {
            id: 'progression-hierarchy',
            title: 'Progression Hierarchy',
            type: 'item',
            icon: 'sitemap',
            url: 'app/classroom-progression/hierarchy',
            role: [ApiRoutes.api.classroom.retrieve],
          },
        ],
      },
      {
        id: 'courses',
        title: 'Courses',
        type: 'collapsible',
        icon: 'book-open',
        role: [ApiRoutes.api.course.retrieve],
        children: [
          {
            id: 'courses-list',
            title: 'Courses',
            type: 'item',
            icon: 'book-open',
            url: 'app/courses',
            role: [ApiRoutes.api.course.retrieve],
          },
          {
            id: 'credit-value-management',
            title: 'Credit Value Management',
            type: 'item',
            icon: 'pi pi-edit',
            url: 'app/courses/credit-values',
            role: [ApiRoutes.api.course.edit],
          },
        ],
      },
      {
        id: 'course-assignment',
        title: 'Course Assignment',
        type: 'item',
        icon: 'user-check',
        url: 'app/course-assignment',
        role: [ApiRoutes.api.course.assignCourses],
      },
      {
        id: 'course-registration',
        title: 'Course Registration',
        type: 'item',
        icon: 'bookmark-plus',
        url: 'app/courses/register',
        role: [ApiRoutes.api.course.retrieve],
      },
      {
        id: 'academic-level',
        title: 'Academic Levels',
        type: 'item',
        icon: 'list',
        url: 'app/academic-level',
        role: [ApiRoutes.api.academicLevel.retrieve],
      },
      {
        id: 'semester',
        title: 'Semesters',
        type: 'item',
        icon: 'file',
        url: 'app/semester',
        role: [ApiRoutes.api.semester.retrieve],
      },
      {
        id: 'sequence',
        title: 'Sequences',
        type: 'item',
        icon: 'sliders',
        url: 'app/sequence',
        role: [ApiRoutes.api.sequence.retrieve],
      },
      {
        id: 'series',
        title: 'Series',
        type: 'item',
        icon: 'move',
        url: 'app/series',
        role: [ApiRoutes.api.series.retrieve],
      },
      {
        id: 'specialty',
        title: 'Specialization',
        type: 'item',
        icon: 'tool',
        url: 'app/specialty',
        role: [ApiRoutes.api.specialization.retrieve],
      },
      {
        id: 'departments',
        title: 'Departments',
        type: 'item',
        icon: 'sunset',
        url: 'app/departments',
        role: [ApiRoutes.api.department.retrieve],
      },
    ]
  },

  // Teaching & Learning - Accessible to Staff & Learners
  (isStaff() || isLearner() || isAdmin()) && {
    id: 'teaching-learning',
    title: 'Teaching & Learning',
    type: 'collapsible',
    icon: 'graduation-cap',
    role: [ApiRoutes.api.lecture.retrieve, ApiRoutes.api.assignment.retrieve, ApiRoutes.api.timeTable.retrieve],
    children: [
      {
        id: 'assignments',
        title: 'Assignments',
        type: 'item',
        icon: 'activity',
        url: 'app/assignments',
        role: [ApiRoutes.api.assignment.retrieve],
      },
      {
        id: 'lectures',
        title: 'Lectures',
        type: 'item',
        icon: 'book',
        url: 'app/lectures',
        role: [ApiRoutes.api.lecture.retrieve],
      },
      {
        id: 'timetable',
        title: 'Timetable',
        type: 'item',
        icon: 'layout',
        url: 'app/timetable',
        role: [ApiRoutes.api.timeTable.retrieve],
      },
      {
        id: 'evaluations',
        title: 'Evaluations',
        type: 'item',
        icon: 'edit-2',
        url: 'app/evaluations',
        role: [ApiRoutes.api.evaluation.retrieve],
      },
      {
        id: 'custom-exams',
        title: 'Custom Exams',
        translate: 'MENU.CUSTOM_EXAMS',
        type: 'item',
        icon: 'file-text',
        url: 'app/custom-exam',
        role: [ApiRoutes.api.evaluation.retrieve],
      },
      isAdmin() && {
        id: 'gradebook',
        title: 'Gradebook',
        type: 'item',
        icon: 'clipboard',
        url: 'app/evaluations/gradebook',
        role: [ApiRoutes.api.evaluation.retrieve],
      },
      {
        id: 'results',
        title: 'Results',
        type: 'item',
        icon: 'clipboard',
        url: 'app/results',
        role: [ApiRoutes.api.result.getTermPerformanceSummary],
      },
    ]
  },

  // Student Management - Admin & Staff Access
  (isStaff() || isAdmin()) && {
    id: 'student-management',
    title: 'Student Management',
    type: 'collapsible',
    icon: 'users',
    role: [ApiRoutes.api.learner.retrieve, ApiRoutes.api.teacher.retrieve],
    children: [
      {
        id: 'learners',
        title: 'Learners',
        type: 'item',
        icon: 'user-check',
        url: 'app/learners',
        role: [ApiRoutes.api.learner.retrieve],
      },
      {
        id: 'student-registration',
        title: 'Student Registration',
        type: 'item',
        icon: 'user-plus',
        url: 'app/learners/register',
        role: [ApiRoutes.api.learner.retrieve],
      },
      {
        id: 'staff',
        title: 'Staff',
        type: 'item',
        icon: 'users',
        url: 'app/staff',
        role: [ApiRoutes.api.teacher.retrieve],
      },
      {
        id: 'promotion',
        title: 'Student Promotion',
        type: 'item',
        icon: 'arrow-up-circle',
        url: 'app/promotion',
        role: [ApiRoutes.api.promotion.promoteClass],
      },
      {
        id: 'progression-wizard',
        title: 'Progression Wizard',
        type: 'item',
        icon: 'zap',
        url: 'app/classroom-progression/wizard',
        role: [ApiRoutes.api.classroom.edit],
      },
      {
        id: 'progression-analytics',
        title: 'Progression Analytics',
        type: 'item',
        icon: 'bar-chart-2',
        url: 'app/classroom-progression/analytics',
        role: [ApiRoutes.api.classroom.retrieve],
      },
    ]
  },

  // Administrative - Admin Only Access
  (isAdmin()) && {
    id: 'administrative',
    title: 'Administrative',
    type: 'collapsible',
    icon: 'settings',
    role: [ApiRoutes.api.role.retrieve, ApiRoutes.api.fees.retrieve],
    children: [
      // {
      //   id: 'id-card-demo',
      //   title: 'ID Card Demo',
      //   type: 'item',
      //   icon: 'id-card',
      //   url: 'app/id-card-demo',
      //   role: [ApiRoutes.api.role.retrieve],
      // },
      {
        id: 'roles',
        title: 'Roles & Permissions',
        type: 'item',
        icon: 'refresh-ccw',
        url: 'app/roles',
        role: [ApiRoutes.api.role.retrieve],
      },
      {
        id: 'fees',
        title: 'Fees Management',
        type: 'item',
        icon: 'dollar-sign',
        url: 'app/fees',
        role: [ApiRoutes.api.fees.retrieve]
      },
      {
        id: 'account-settings',
        title: 'System Settings',
        type: 'item',
        icon: 'settings',
        url: 'app/admin/settings',
        role: [ApiRoutes.api.user.retrieveSingle],
      },
    ]
  },
];
